import { useRef, type MouseEvent, type RefObject, type TouchEvent } from 'react';

interface DragState {
  isDragging: boolean;
  startX: number;
  scrollLeft: number;
}

interface UseHorizontalDragScrollReturn {
  scrollRef: RefObject<HTMLDivElement>;
  onMouseDown: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseMove: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onTouchStart: (event: TouchEvent<HTMLDivElement>) => void;
  onTouchMove: (event: TouchEvent<HTMLDivElement>) => void;
  onTouchEnd: () => void;
}

const IGNORE_DRAG_SELECTOR = 'button, a, input, textarea, select, label, svg, img';

export const useHorizontalDragScroll = (): UseHorizontalDragScrollReturn => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<DragState>({
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
  });

  const shouldIgnoreDrag = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return Boolean(target.closest(IGNORE_DRAG_SELECTOR));
  };

  const handlePointerDown = (clientX: number, target: EventTarget | null): void => {
    if (!scrollRef.current || shouldIgnoreDrag(target)) {
      return;
    }

    dragStateRef.current = {
      isDragging: true,
      startX: clientX,
      scrollLeft: scrollRef.current.scrollLeft,
    };
  };

  const handlePointerMove = (clientX: number): void => {
    if (!scrollRef.current || !dragStateRef.current.isDragging) {
      return;
    }

    const distance = clientX - dragStateRef.current.startX;
    scrollRef.current.scrollLeft = dragStateRef.current.scrollLeft - distance;
  };

  const handlePointerUp = (): void => {
    dragStateRef.current.isDragging = false;
  };

  return {
    scrollRef,
    onMouseDown: event => handlePointerDown(event.clientX, event.target),
    onMouseMove: event => handlePointerMove(event.clientX),
    onMouseUp: handlePointerUp,
    onMouseLeave: handlePointerUp,
    onTouchStart: event => handlePointerDown(event.touches[0].clientX, event.target),
    onTouchMove: event => handlePointerMove(event.touches[0].clientX),
    onTouchEnd: handlePointerUp,
  };
};
